import { supabase, type Json, type Tables } from '@xanectech/supabase-client';

import type {
  DataCategory,
  FieldDefinition,
  DataCategoryInsert,
  DataCategoryUpdate,
  FieldValidationAssignment,
  UnifiedFieldType,
} from '@xanectech/types';
import { createLogger } from '@xanectech/lib';
import { toast } from '@xanectech/ui/lib';

const logger = createLogger('Data - AdminDataCategoryManagementService');

type FieldDefinitionRow = Tables<'field_definitions'>;
type CountryContextRule = NonNullable<
  FieldDefinition['country_context_rules']
>[string];

type FieldDefinitionWriteInput = Omit<
  FieldDefinition,
  'id' | 'created_at' | 'updated_at'
>;

/**
 * Admin Data Category Management Service
 *
 * Purpose: Manage data categories and field definitions that companies use for
 * data sharing requests and users use to capture and share data.
 *
 * This is NOT about user identity verification or user data auditing.
 * This is about managing the data structure/schema that the system uses.
 */
export class AdminDataCategoryManagementService {
  private static normalizeCountryContextRules(
    value: Json | null,
  ): Record<string, CountryContextRule> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return undefined;
    }

    const result: Record<string, CountryContextRule> = {};

    for (const [countryCode, rule] of Object.entries(value)) {
      if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
        continue;
      }

      const maybeCountryCode =
        'country_code' in rule && typeof rule.country_code === 'string'
          ? rule.country_code
          : countryCode;

      if (!maybeCountryCode) {
        continue;
      }

      const normalizedRule: CountryContextRule = {
        country_code: maybeCountryCode,
      };

      if ('is_required' in rule && typeof rule.is_required === 'boolean') {
        normalizedRule.is_required = rule.is_required;
      }

      if ('pattern' in rule && typeof rule.pattern === 'string') {
        normalizedRule.pattern = rule.pattern;
      }

      if (
        'validation_message' in rule &&
        typeof rule.validation_message === 'string'
      ) {
        normalizedRule.validation_message = rule.validation_message;
      }

      result[countryCode] = normalizedRule;
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private static normalizeOptions(
    value: Json | null,
  ): FieldDefinition['options'] | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const options: FieldDefinition['options'] = {};

    // Preserve array-based option payloads by normalizing into key-value pairs.
    // FieldDefinitionForm supports object and array shapes, but the domain type here
    // expects a record, so we retain compatibility by converting arrays to records.
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        const item = value[index];

        if (typeof item === 'string' || typeof item === 'number') {
          const key = String(item);
          options[key] = key;
          continue;
        }

        if (!item || typeof item !== 'object' || Array.isArray(item)) {
          continue;
        }

        const option = item as Record<string, unknown>;

        // Current format: { value: string, label: string }
        if ('value' in option && option.value !== undefined) {
          const key = String(option.value);
          const label =
            typeof option.label === 'string' || typeof option.label === 'number'
              ? String(option.label)
              : key;
          if (key) {
            options[key] = label;
          }
          continue;
        }

        // Legacy format: { key: string, value: string }
        if ('key' in option && option.key !== undefined) {
          const key = String(option.key);
          const label =
            typeof option.value === 'string' || typeof option.value === 'number'
              ? String(option.value)
              : key;
          if (key) {
            options[key] = label;
          }
          continue;
        }

        // Fallback for malformed objects
        const fallbackKey = `option_${index}`;
        options[fallbackKey] = fallbackKey;
      }

      return Object.keys(options).length > 0 ? options : undefined;
    }

    for (const [key, optionValue] of Object.entries(value)) {
      if (
        optionValue === null ||
        typeof optionValue === 'string' ||
        typeof optionValue === 'number' ||
        typeof optionValue === 'boolean'
      ) {
        options[key] = optionValue;
      }
    }

    return Object.keys(options).length > 0 ? options : undefined;
  }

  private static toUnifiedFieldType(
    value: FieldDefinitionRow['data_type'],
  ): UnifiedFieldType {
    // Map DB-only variant to supported domain variant
    if (value === 'address_autocomplete') {
      return 'address';
    }

    // If this is the only extra DB value, TS will narrow this to UnifiedFieldType
    return value as UnifiedFieldType;
  }

  private static toFieldDefinition(field: FieldDefinitionRow): FieldDefinition {
    return {
      ...field,
      data_type: this.toUnifiedFieldType(field.data_type),
      company_id: field.company_id ?? undefined,
      country_context_rules: this.normalizeCountryContextRules(
        field.country_context_rules,
      ),
      created_at: field.created_at ?? '',
      default_value: field.default_value ?? undefined,
      depends_on_field_id: field.depends_on_field_id ?? undefined,
      description: field.description ?? undefined,
      display_order: field.display_order ?? 0,
      field_group: field.field_group ?? undefined,
      help_text: field.help_text ?? undefined,
      is_active: field.is_active ?? true,
      is_pii: field.is_pii ?? false,
      is_required: field.is_required,
      is_sensitive: field.is_sensitive ?? false,
      is_system_field: field.is_system_field ?? false,
      max_length: field.max_length ?? undefined,
      max_value: field.max_value ?? undefined,
      min_length: field.min_length ?? undefined,
      min_value: field.min_value ?? undefined,
      options: this.normalizeOptions(field.options),
      pattern: field.pattern ?? undefined,
      placeholder: field.placeholder ?? undefined,
      requires_country_context: field.requires_country_context ?? false,
      updated_at: field.updated_at ?? '',
      validation_message: field.validation_message ?? undefined,
      is_exportable: field.is_exportable ?? true,
      is_importable: field.is_importable ?? true,
    };
  }

  /**
   * Get all data categories with field counts and statistics
   */
  static async getAllCategories(): Promise<DataCategory[]> {
    try {
      const { data, error } = await supabase
        .from('data_categories')
        .select(
          `
          *,
          field_definitions!field_definitions_category_id_fkey(count)
        `,
        )
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      return (
        data?.map((category) => ({
          ...category,
          icon: category.icon ?? 'Folder',
          allows_multiple_entries: category.allows_multiple_entries ?? false,
          is_system_category: category.is_system_category ?? false,
          is_mandatory: category.is_mandatory ?? false,
          is_active: category.is_active ?? true,
          display_order: category.display_order ?? 0,
          max_entries: category.max_entries ?? undefined,
          color_theme: category.color_theme ?? '#6366f1',
          type_field_id: category.type_field_id ?? undefined,
          created_at: category.created_at ?? new Date().toISOString(),
          updated_at: category.updated_at ?? new Date().toISOString(),
          field_count: category.field_definitions?.[0]?.count || 0,
        })) || []
      );
    } catch (error) {
      logger.error('Failed to fetch data categories:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to load data categories');
      throw error;
    }
  }

  /**
   * Get all field definitions for a specific category
   */
  static async getCategoryFieldDefinitions(
    categoryId: string,
  ): Promise<FieldDefinition[]> {
    try {
      const { data, error } = await supabase
        .from('field_definitions')
        .select('*')
        .eq('category_id', categoryId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return (data || []).map((field) => this.toFieldDefinition(field));
    } catch (error) {
      logger.error('Failed to fetch field definitions:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to load field definitions');
      throw error;
    }
  }

  /**
   * Create a new data category
   */
  static async createCategory(
    categoryData: DataCategoryInsert,
  ): Promise<DataCategory> {
    try {
      const { data, error } = await supabase
        .from('data_categories')
        .insert(categoryData)
        .select()
        .single();

      if (error) throw error;

      logger.info('Data category created successfully');
      return {
        ...data,
        icon: data.icon ?? 'Folder',
        allows_multiple_entries: data.allows_multiple_entries ?? false,
        is_system_category: data.is_system_category ?? false,
        is_mandatory: data.is_mandatory ?? false,
        is_active: data.is_active ?? true,
        display_order: data.display_order ?? 0,
        max_entries: data.max_entries ?? undefined,
        color_theme: data.color_theme ?? '#6366f1',
        type_field_id: data.type_field_id ?? undefined,
        created_at: data.created_at ?? new Date().toISOString(),
        updated_at: data.updated_at ?? new Date().toISOString(),
      };
    } catch (error) {
      logger.error('Failed to create data category:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to create data category');
      throw error;
    }
  }

  /**
   * Update a data category
   */
  static async updateCategory(
    id: string,
    updates: DataCategoryUpdate,
  ): Promise<DataCategory> {
    try {
      const { data, error } = await supabase
        .from('data_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      logger.info('Data category updated successfully');
      return {
        ...data,
        icon: data.icon ?? 'Folder',
        allows_multiple_entries: data.allows_multiple_entries ?? false,
        is_system_category: data.is_system_category ?? false,
        is_mandatory: data.is_mandatory ?? false,
        is_active: data.is_active ?? true,
        display_order: data.display_order ?? 0,
        max_entries: data.max_entries ?? undefined,
        color_theme: data.color_theme ?? '#6366f1',
        type_field_id: data.type_field_id ?? undefined,
        created_at: data.created_at ?? new Date().toISOString(),
        updated_at: data.updated_at ?? new Date().toISOString(),
      };
    } catch (error) {
      logger.error('Failed to update data category:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to update data category');
      throw error;
    }
  }

  /**
   * Delete a data category (and all its field definitions)
   */
  static async deleteCategory(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('data_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // toast.success('Data category deleted successfully');
    } catch (error) {
      logger.error('Failed to delete data category:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to delete data category');
      throw error;
    }
  }

  /**
   * Create a new field definition
   */
  static async createFieldDefinition(
    fieldData: Omit<FieldDefinition, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<FieldDefinition> {
    try {
      const { data, error } = await supabase
        .from('field_definitions')
        .insert(fieldData as FieldDefinitionWriteInput)
        .select()
        .single();

      if (error) throw error;

      // toast.success('Field definition created successfully');
      return this.toFieldDefinition(data);
    } catch (error) {
      logger.error('Failed to create field definition:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to create field definition');
      throw error;
    }
  }

  /**
   * Update a field definition
   */
  static async updateFieldDefinition(
    id: string,
    updates: Partial<Omit<FieldDefinition, 'id' | 'created_at' | 'updated_at'>>,
  ): Promise<FieldDefinition> {
    try {
      const { data, error } = await supabase
        .from('field_definitions')
        .update(updates as Partial<FieldDefinitionWriteInput>)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // toast.success('Field definition updated successfully');
      return this.toFieldDefinition(data);
    } catch (error) {
      logger.error('Failed to update field definition:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to update field definition');
      throw error;
    }
  }

  /**
   * Delete a field definition
   */
  static async deleteFieldDefinition(id: string): Promise<void> {
    try {
      // Check for dependent fields before deletion
      const { data, error } = await supabase
        .from('field_definitions')
        .select('id, field_label')
        .eq('depends_on_field_id', id);

      if (error) throw error;

      if (data && data.length > 0) {
        const dependentLabels = data.map((f) => f.field_label).join(', ');
        // toast.error
        logger.error(
          `Cannot delete this field. The following fields depend on it: ${dependentLabels}. Please delete the dependent fields first.`,
        );
        throw new Error(
          `Field has ${data.length} dependent field(s): ${dependentLabels}`,
        );
      }

      const { error: deleteError } = await supabase
        .from('field_definitions')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // toast.success('Field definition deleted successfully');
    } catch (error) {
      logger.error('Failed to delete field definition:', {
        error: error instanceof Error ? error.message : String(error),
      });
      if (error instanceof Error) {
        if (!error.message?.includes('depend on it')) {
          // toast.error('Failed to delete field definition');
        }
      } else {
        // toast.error('Failed to delete field definition');
      }
      throw error;
    }
  }

  /**
   * Reorder field definitions within a category
   */
  static async reorderFields(
    fieldUpdates: Array<{ id: string; display_order: number }>,
  ): Promise<void> {
    try {
      const promises = fieldUpdates.map((update) =>
        supabase
          .from('field_definitions')
          .update({ display_order: update.display_order })
          .eq('id', update.id),
      );

      await Promise.all(promises);
      // toast.success('Field order updated successfully');
    } catch (error) {
      logger.error('Failed to reorder fields:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to reorder fields');
      throw error;
    }
  }

  /**
   * Reorder data categories
   */
  static async reorderCategories(
    categoryUpdates: Array<{ id: string; display_order: number }>,
  ): Promise<void> {
    try {
      const promises = categoryUpdates.map((update) =>
        supabase
          .from('data_categories')
          .update({ display_order: update.display_order })
          .eq('id', update.id),
      );

      await Promise.all(promises);
      // toast.success('Category order updated successfully');
    } catch (error) {
      logger.error('Failed to reorder categories:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to reorder categories');
      throw error;
    }
  }

  /**
   * Get system statistics for categories and fields
   */
  static async getSystemStats(): Promise<{
    total_categories: number;
    total_fields: number;
    active_categories: number;
    required_fields: number;
    pii_fields: number;
    system_categories: number;
  }> {
    try {
      // Get category counts
      const { count: totalCategories, error: categoriesError } = await supabase
        .from('data_categories')
        .select('*', { count: 'exact', head: true });

      if (categoriesError) throw categoriesError;

      const { count: activeCategories, error: activeCategoriesError } =
        await supabase
          .from('data_categories')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true);

      if (activeCategoriesError) throw activeCategoriesError;

      const { count: systemCategories, error: systemCategoriesError } =
        await supabase
          .from('data_categories')
          .select('*', { count: 'exact', head: true })
          .eq('is_system_category', true);

      if (systemCategoriesError) throw systemCategoriesError;

      // Get field counts
      const { count: totalFields, error: fieldsError } = await supabase
        .from('field_definitions')
        .select('*', { count: 'exact', head: true });

      if (fieldsError) throw fieldsError;

      const { count: requiredFields, error: requiredFieldsError } =
        await supabase
          .from('field_definitions')
          .select('*', { count: 'exact', head: true })
          .eq('is_required', true);

      if (requiredFieldsError) throw requiredFieldsError;

      const { count: piiFields, error: piiFieldsError } = await supabase
        .from('field_definitions')
        .select('*', { count: 'exact', head: true })
        .eq('is_pii', true);

      if (piiFieldsError) throw piiFieldsError;

      return {
        total_categories: totalCategories || 0,
        total_fields: totalFields || 0,
        active_categories: activeCategories || 0,
        required_fields: requiredFields || 0,
        pii_fields: piiFields || 0,
        system_categories: systemCategories || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch system stats:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to load system statistics');
      throw error;
    }
  }

  /**
   * Clone a category with all its field definitions
   */
  static async cloneCategory(
    sourceCategoryId: string,
    newCategoryData: DataCategoryInsert,
  ): Promise<DataCategory> {
    try {
      // Create the new category
      const newCategory = await this.createCategory(newCategoryData);

      // Get all field definitions from source category
      const sourceFields =
        await this.getCategoryFieldDefinitions(sourceCategoryId);

      // Clone all field definitions to new category
      const clonedFields = sourceFields.map((field) => ({
        ...field,
        id: undefined, // Remove ID so new ones are generated
        category_id: newCategory.id,
        created_at: undefined,
        updated_at: undefined,
      }));

      const promises = clonedFields.map((field) =>
        this.createFieldDefinition(
          field as Omit<FieldDefinition, 'id' | 'created_at' | 'updated_at'>,
        ),
      );
      await Promise.all(promises);

      // toast.success(
      //   `Category cloned with ${sourceFields.length} field definitions`
      // );
      return newCategory;
    } catch (error) {
      logger.error('Failed to clone category:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // toast.error('Failed to clone category');
      throw error;
    }
  }

  /**
   * Get the next display order for a new data category
   */
  static async getNextCategoryDisplayOrder(): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('data_categories')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      if (error) throw error;

      const maxOrder = data?.[0]?.display_order || 0;
      return maxOrder + 1;
    } catch (error) {
      logger.error('Failed to get next category display order:', {
        error: error instanceof Error ? error.message : String(error),
      });
      return 0; // Default to 0 if there's an error
    }
  }

  /**
   * Get the next display order for a new field definition in a category
   */
  static async getNextFieldDisplayOrder(categoryId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('field_definitions')
        .select('display_order')
        .eq('category_id', categoryId)
        .order('display_order', { ascending: false })
        .limit(1);

      if (error) throw error;

      const maxOrder = data?.[0]?.display_order || 0;
      return maxOrder + 1;
    } catch (error) {
      logger.error('Failed to get next field display order:', {
        error: error instanceof Error ? error.message : String(error),
      });
      return 0; // Default to 0 if there's an error
    }
  }

  /**
   * Validate that a field can serve as a type field
   * Only select and multiselect fields can be type fields
   */
  static async validateTypeField(
    categoryId: string,
    fieldId: string,
  ): Promise<{ valid: boolean; error?: string }> {
    try {
      const { data: field, error } = await supabase
        .from('field_definitions')
        .select('id, data_type, category_id')
        .eq('id', fieldId)
        .eq('category_id', categoryId)
        .single();

      if (error || !field) {
        return {
          valid: false,
          error: 'Field not found in the specified category',
        };
      }

      if (field.data_type !== 'select' && field.data_type !== 'multiselect') {
        return {
          valid: false,
          error: 'Only select and multiselect fields can serve as type fields',
        };
      }

      return { valid: true };
    } catch (error) {
      logger.error('Error validating type field:', {
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        valid: false,
        error: 'Failed to validate type field',
      };
    }
  }

  /**
   * Get available type field candidates for a category
   * Returns select and multiselect fields that can serve as type fields
   */
  static async getTypeFieldCandidates(
    categoryId: string,
  ): Promise<FieldDefinition[]> {
    try {
      const fields = await this.getCategoryFieldDefinitions(categoryId);
      return fields.filter(
        (field) =>
          field.data_type === 'select' || field.data_type === 'multiselect',
      );
    } catch (error) {
      logger.error('Failed to get type field candidates:', {
        error: error instanceof Error ? error.message : String(error),
      });
      return [];
    }
  }

  /**
   * Update category with type field validation
   * Ensures type field is valid before updating
   */
  static async updateCategoryWithTypeField(
    categoryId: string,
    updates: DataCategoryUpdate,
    typeFieldId?: string | null,
  ): Promise<DataCategory> {
    try {
      // If setting a type field, validate it first
      if (typeFieldId) {
        const validation = await this.validateTypeField(
          categoryId,
          typeFieldId,
        );
        if (!validation.valid) {
          throw new Error(validation.error || 'Invalid type field');
        }
      }

      // Update the category with the type field
      const categoryUpdates: DataCategoryUpdate = {
        ...updates,
        type_field_id: typeFieldId,
      };

      return await this.updateCategory(categoryId, categoryUpdates);
    } catch (error) {
      logger.error('Failed to update category with type field:', {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  // ==========================================
  // Field Validation Context Assignments
  // ==========================================

  /**
   * Get validation context assignments for a field
   */
  static async getFieldValidationAssignments(fieldId: string): Promise<
    Array<{
      id: string;
      field_id: string;
      validation_function_name: string;
      context_name: string;
      applies_when_countries: string[] | null;
      excludes_countries: string[] | null;
      applies_when_document_types: string[] | null;
      excludes_document_types: string[] | null;
      validation_config: Record<string, unknown>;
      priority: number | null;
      is_required: boolean | null;
      error_message_override: string | null;
    }>
  > {
    try {
      const { data, error } = await supabase
        .from('field_validation_context_assignments')
        .select('*')
        .eq('field_id', fieldId)
        .order('priority', { ascending: false });

      if (error) throw error;

      return (data || []).map((row) => ({
        id: row.id,
        field_id: row.field_id,
        validation_function_name: row.validation_function_name,
        context_name: row.context_name,
        applies_when_countries: row.applies_when_countries,
        excludes_countries: row.excludes_countries,
        applies_when_document_types: row.applies_when_document_types,
        excludes_document_types: row.excludes_document_types,
        validation_config:
          (row.validation_config as Record<string, unknown>) || {},
        priority: row.priority,
        is_required: row.is_required,
        error_message_override: row.error_message_override,
      }));
    } catch (error) {
      logger.error('Failed to fetch field validation assignments:', {
        error: error instanceof Error ? error.message : String(error),
      });
      return [];
    }
  }

  /**
   * Save validation context assignments for a field
   * This replaces all existing assignments with the new ones
   */

  static async saveFieldValidationAssignments(
    fieldId: string,
    assignments: Array<FieldValidationAssignment>,
  ): Promise<void> {
    try {
      // First, get or create validation rules for each function name
      const validationRuleIds = new Map<string, string>();
      for (const assignment of assignments) {
        if (!validationRuleIds.has(assignment.validationFunctionName)) {
          const ruleId = await this.getOrCreateValidationRule(
            assignment.validationFunctionName,
          );
          validationRuleIds.set(assignment.validationFunctionName, ruleId);
        }
      }

      // Delete existing assignments for this field
      const { error: deleteError } = await supabase
        .from('field_validation_context_assignments')
        .delete()
        .eq('field_id', fieldId);

      if (deleteError) throw deleteError;

      // Insert new assignments if there are any
      if (assignments.length > 0) {
        const insertData = assignments.map((assignment) => ({
          field_id: fieldId,
          validation_function_name: assignment.validationFunctionName,
          validation_rule_id: validationRuleIds.get(
            assignment.validationFunctionName,
          )!,
          context_name: assignment.contextName,
          applies_when_countries: assignment.appliesWhenCountries || null,
          excludes_countries: assignment.excludesCountries || null,
          applies_when_document_types:
            assignment.appliesWhenDocumentTypes || null,
          excludes_document_types: assignment.excludesDocumentTypes || null,
          validation_config: assignment.validationConfig || {},
          priority: assignment.priority ?? 0,
          is_required: assignment.isRequired ?? false,
          error_message_override: assignment.errorMessageOverride || null,
        }));

        const { error: insertError } = await supabase
          .from('field_validation_context_assignments')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      logger.info(
        `Saved ${assignments.length} validation assignments for field ${fieldId}`,
      );
    } catch (error) {
      logger.error('Failed to save field validation assignments:', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: 'Failed to save validation assignments',
        variant: 'error',
      });
      throw error;
    }
  }

  /**
   *
  ): Promise<void> {
    try {
      // First, get or create validation rules for each function name
      const validationRuleIds = new Map<string, string>();

      for (const assignment of assignments) {
        if (!validationRuleIds.has(assignment.validationFunctionName)) {
          const ruleId = await this.getOrCreateValidationRule(
            assignment.validationFunctionName,
          );
          validationRuleIds.set(assignment.validationFunctionName, ruleId);
        }
      }

      // Delete existing assignments for this field
      const { error: deleteError } = await supabase
        .from('field_validation_context_assignments')
        .delete()
        .eq('field_id', fieldId);

      if (deleteError) throw deleteError;

      // Insert new assignments if there are any
      if (assignments.length > 0) {
        const insertData = assignments.map((assignment) => ({
          field_id: fieldId,
          validation_function_name: assignment.validationFunctionName,
          validation_rule_id: validationRuleIds.get(
            assignment.validationFunctionName,
          )!,
          context_name: assignment.contextName,
          applies_when_countries: assignment.appliesWhenCountries || null,
          excludes_countries: assignment.excludesCountries || null,
          applies_when_document_types:
            assignment.appliesWhenDocumentTypes || null,
          excludes_document_types: assignment.excludesDocumentTypes || null,
          validation_config: assignment.validationConfig || {},
          priority: assignment.priority ?? 0,
          is_required: assignment.isRequired ?? false,
          error_message_override: assignment.errorMessageOverride || null,
        }));

        const { error: insertError } = await supabase
          .from('field_validation_context_assignments')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      logger.info(
        `Saved ${assignments.length} validation assignments for field ${fieldId}`,
      );
    } catch (error) {
      logger.error('Failed to save field validation assignments:', { error });
      toast({
        title: 'Failed to save validation assignments',
        variant: 'error',
      });
      throw error;
    }
  }

  /**
   * Get or create a validation rule for a function name
   */
  private static async getOrCreateValidationRule(
    functionName: string,
  ): Promise<string> {
    try {
      // Check if rule already exists
      const { data: existing, error: selectError } = await supabase
        .from('data_validation_rules')
        .select('id')
        .eq('function_name', functionName)
        .maybeSingle();

      if (selectError) throw selectError;

      if (existing) {
        return existing.id;
      }

      // Create a new rule for this function
      const { data: newRule, error: insertError } = await supabase
        .from('data_validation_rules')
        .insert({
          rule_name: functionName,
          rule_type: 'function' as const,
          function_name: functionName,
          error_message: `Validation failed for ${functionName}`,
          is_system_rule: true,
          is_active: true,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      return newRule.id;
    } catch (error) {
      logger.error(
        `Failed to get/create validation rule for ${functionName}:`,
        { error: error instanceof Error ? error.message : String(error) },
      );
      throw error;
    }
  }
}
